import { useMemo } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { FeaturesSortingOption } from '../models';
import { getFeatures } from '../client';
import type { FeatureResponse } from '../client';

import { useTeam } from './useTeam';
import { useFeaturesSortingOption } from './useFeaturesSortingOption';
import { useSimulationId } from './useSimulationId';

export function useSortedFeaturesWithTeamContext(): UseQueryResult<FeatureResponse[], Error> {
  const team = useTeam();
  const simId = useSimulationId();
  const { sortFeaturesBy } = useFeaturesSortingOption();

  const query = useQuery({
    queryKey: ['features-with-team-context', simId, team.name],
    queryFn: () =>
      getFeatures({ path: { sim_id: simId }, query: { team: team.name }, throwOnError: true }).then(({ data }) => data),
    refetchInterval: 30_000,
  });

  const sortedData = useMemo(() => {
    return query.data !== undefined ? sortFeatures(query.data, sortFeaturesBy) : undefined;
  }, [query.data, sortFeaturesBy]);

  return { ...query, data: sortedData as any };
}

/** Extract the numeric part from a `PREFIX-NUMBER` ID (e.g. "F-27" → 27). */
function parseFeatureNum(id: string): number {
  return parseInt(id.match(/^\w+-(\d+)$/)?.[1] || '0', 10);
}

export function sortFeatures(features: FeatureResponse[], sortFeaturesBy: FeaturesSortingOption): FeatureResponse[] {
  switch (sortFeaturesBy) {
    case 'id':
      return features.slice().sort((a, b) => parseFeatureNum(a.id) - parseFeatureNum(b.id));
    case 'id-desc':
      return features.slice().sort((a, b) => parseFeatureNum(b.id) - parseFeatureNum(a.id));
    case 'size':
      return features.slice().sort((a, b) => a.size - b.size);
    case 'size-desc':
      return features.slice().sort((a, b) => b.size - a.size);
    case 'value':
      return features.slice().sort((a, b) => b.earnings_rate - a.earnings_rate);
    case 'value-desc':
      return features.slice().sort((a, b) => a.earnings_rate - b.earnings_rate);
    case 'weighted-value':
      return features.slice().sort((a, b) => b.pending_tasks * b.earnings_rate - a.pending_tasks * a.earnings_rate);
    case 'weighted-value-desc':
      return features.slice().sort((a, b) => a.pending_tasks * a.earnings_rate - b.pending_tasks * b.earnings_rate);
    default:
      throw Error(`Sort option not implemented: ${sortFeaturesBy}`);
  }
}
